import React, {useMemo, useEffect, useState, useCallback, useRef} from "react";
import {StyledLiveOrders, NewOrderOverlay, NewOrderBanner} from "components/atoms/LiveOrders/LiveOrders.styles";
import clockIcon from "assets/LiveOrders/clock-icon.svg";
import dishImg from "assets/LiveOrders/dish.png";
import Select from "components/molecules/Select";
import {useAuthContext} from "Context/authContext";
import orderService from "services/orderService";
import {useSelector, useDispatch} from "react-redux";
import OrderCard from "./OrderCard";
import Button from "components/molecules/Button";
import {IoIosArrowDown} from "react-icons/io";
import Form from "components/molecules/Form/Form";
import Field from "components/molecules/Field";
import {useForm} from "components/molecules/Form";
import {formatApiOrder} from "helpers/useCountDown";
import menuStoreService from "services/menu-storeService";
import {setSelectedStoreId} from "store/reducers/businessSlice";
import foodie from "assets/Home/foodie.svg";
import Pagination from "components/molecules/Pagination";
import Loaders from "components/atoms/Loaders";


const newOrderRingtone = "https://foodie-bucket-live.s3.ca-central-1.amazonaws.com/Sound/universfield-ringtone-046-494552.mp3";

const LiveOrder = () => {
    const [form] = useForm();
    const dispatch = useDispatch();
    const business = useSelector(state => state.business.id);
    const {user} = useAuthContext();
    const businessIdFromAuth = user?.businessId;
    const businessId = businessIdFromAuth || business;
    const defaultStore = useSelector(state => state.business.defaultStore);
    const selectedStoreIdFromRedux = useSelector(state => state.business.selectedStoreId);
    const userType = useSelector(state => state.business.userType);
    const [selectedStore, setSelectedStore] = useState("");
    const [allStores, setAllStores] = useState([]);
    const [currentStoreId, setCurrentStoreId] = useState(selectedStoreIdFromRedux || defaultStore);
    const [orders, setOrders] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState({label: "All Status", value: "All Status"});
    const [typeFilter, setTypeFilter] = useState({label: "All Type", value: "All Type"});
    const [customDateRange, setCustomDateRange] = useState({startDate: "", endDate: ""});
    const [appliedDates, setAppliedDates] = useState({startDate: "", endDate: ""});

    // New Order Overlay State
    const [newOrderCount, setNewOrderCount] = useState(0);
    const [showOverlay, setShowOverlay] = useState(false);
    const [showBanner, setShowBanner] = useState(false);
    const overlayTimerRef = useRef(null);
    const audioRef = useRef(null);
    const [isAudioUnlocked, setIsAudioUnlocked] = useState(false);



    const {fetch} = useAuthContext();

    // ─── New Order Sound & Overlay Logic ──────────────────────────────────

    // Initialize audio element for looping notification
    useEffect(() => {
        const audio = new Audio(newOrderRingtone);
        audio.loop = true;
        audio.volume = 0.8;
        audioRef.current = audio;

        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                audioRef.current = null;
            }
            if (overlayTimerRef.current) {
                clearTimeout(overlayTimerRef.current);
            }
        };
    }, []);

    // Start looping sound
    const startNotificationSound = useCallback(() => {
        try {
            if (audioRef.current) {
                audioRef.current.currentTime = 0;
                const playPromise = audioRef.current.play();
                if (playPromise !== undefined) {
                    playPromise.then(() => {
                        setIsAudioUnlocked(true);
                        console.log("🔔 [LiveOrders] Notification sound started (looping)");
                    }).catch(err => {
                        console.warn("🔔 [LiveOrders] Audio autoplay blocked by browser:", err.message);
                        setIsAudioUnlocked(false);
                    });
                }
            }
        } catch (error) {
            console.error("🔔 [LiveOrders] Error starting notification sound:", error);
        }
    }, []);

    // Unlock audio on first user interaction
    const unlockAudio = useCallback(() => {
        if (isAudioUnlocked || !audioRef.current) return;

        console.log("🔔 [LiveOrders] Attempting to unlock audio...");
        // Play a tiny bit of sound to unlock the audio context
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
            playPromise.then(() => {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                setIsAudioUnlocked(true);
                console.log("🔔 [LiveOrders] Audio successfully unlocked");
            }).catch(err => {
                console.warn("🔔 [LiveOrders] Failed to unlock audio:", err.message);
            });
        }
    }, [isAudioUnlocked]);

    // Global listener for first interaction
    useEffect(() => {
        const handleInteraction = () => {
            unlockAudio();
            // Remove listeners once unlocked
            document.removeEventListener("click", handleInteraction);
            document.removeEventListener("touchstart", handleInteraction);
            document.removeEventListener("keydown", handleInteraction);
        };

        if (!isAudioUnlocked) {
            document.addEventListener("click", handleInteraction);
            document.addEventListener("touchstart", handleInteraction);
            document.addEventListener("keydown", handleInteraction);
        }

        return () => {
            document.removeEventListener("click", handleInteraction);
            document.removeEventListener("touchstart", handleInteraction);
            document.removeEventListener("keydown", handleInteraction);
        };
    }, [unlockAudio, isAudioUnlocked]);

    // Stop looping sound
    const stopNotificationSound = useCallback(() => {
        try {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                console.log("🔔 [LiveOrders] Notification sound stopped");
            }
        } catch (error) {
            console.error("🔔 [LiveOrders] Error stopping notification sound:", error);
        }
    }, []);

    // Show the overlay and start sound for new order(s)
    const triggerNewOrderAlert = useCallback((count) => {
        setNewOrderCount(prev => prev + count);
        setShowOverlay(true);
        setShowBanner(false);
        const soundEnabled = localStorage.getItem("foodie_sound_enabled") !== "false";
        if (soundEnabled) {
            startNotificationSound();
        }

        // Clear any existing collapse timer
        if (overlayTimerRef.current) {
            clearTimeout(overlayTimerRef.current);
        }

        // Auto-collapse overlay to banner after 30 seconds
        overlayTimerRef.current = setTimeout(() => {
            setShowOverlay(false);
            setShowBanner(true);
            // Sound keeps playing during banner mode
            console.log("🔔 [LiveOrders] Overlay auto-collapsed to banner");
        }, 30000);
    }, [startNotificationSound]);

    // Dismiss overlay/banner entirely
    const dismissNewOrderAlert = useCallback(() => {
        setShowOverlay(false);
        setShowBanner(false);
        setNewOrderCount(0);
        stopNotificationSound();

        if (overlayTimerRef.current) {
            clearTimeout(overlayTimerRef.current);
            overlayTimerRef.current = null;
        }
        console.log("🔔 [LiveOrders] New order alert dismissed");
    }, [stopNotificationSound]);

    // Legacy wrapper for compatibility with handleOrderCreated
    const playNewOrderSound = useCallback(() => {
        // Sound is now managed by triggerNewOrderAlert
        // This is kept for any other callers
    }, []);

    const stores = useMemo(() => {
        if (!allStores || allStores.length === 0) {
            console.log("🏪 No stores available for dropdown");
            return [];
        }

        if (userType === "restaurant_employee" && defaultStore) {
            const matchingStore = allStores.find(store => store._id === defaultStore);
            console.log("🏪 Employee mode - matching store:", matchingStore);
            return matchingStore
                ? [
                      {
                          value: matchingStore._id,
                          label: matchingStore.title || matchingStore.name,
                      },
                  ]
                : [];
        }

        const storeOptions = allStores.map(store => ({
            value: store._id,
            label: store.title || store.name,
        }));
        return storeOptions;
    }, [allStores, userType, defaultStore]);

    useEffect(() => {
        // Only apply dates if both are selected, or if both are cleared
        if ((customDateRange.startDate && customDateRange.endDate) || (!customDateRange.startDate && !customDateRange.endDate)) {
            setAppliedDates(customDateRange);
        }
    }, [customDateRange]);

    const searchQuery = useMemo(() => {
        return {
            page: currentPage,
            pageSize: pageSize,
            searchText: searchText,
            startDate: appliedDates.startDate,
            endDate: appliedDates.endDate,
            status: statusFilter.value,
            orderType: typeFilter.value,
            restaurantId: businessId || "",
            storeId: currentStoreId || "",
        };
    }, [businessId, currentStoreId, currentPage, pageSize, searchText, appliedDates, statusFilter, typeFilter]);



    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, typeFilter, appliedDates, searchText, currentStoreId]);



    // Keep a ref to searchQuery so it's always current inside callbacks/timeouts
    const searchQueryRef = useRef(searchQuery);
    useEffect(() => {
        searchQueryRef.current = searchQuery;
    }, [searchQuery]);


    // Fetch all stores for dropdown
    const getAllStores = async () => {
        try {
            const id = businessId || (Array.isArray(user?.businessId) ? user.businessId[0] : null);
            const res = await menuStoreService.getAllstores(id);

            const storesData = res?.items || res?.data?.items || [];
            setAllStores(storesData);

            if (storesData.length > 0) {
                const hasValidSelection = storesData.some(s => s._id === currentStoreId);
                const hasValidReduxSelection = storesData.some(s => s._id === selectedStoreIdFromRedux);

                let storeToSelect = null;

                if (hasValidReduxSelection) {
                    storeToSelect = selectedStoreIdFromRedux;
                } else if (hasValidSelection) {
                    storeToSelect = currentStoreId;
                } else {
                    const defaultStoreObj = storesData.find(s => s.default_branch);
                    storeToSelect = defaultStoreObj ? defaultStoreObj._id : storesData[0]._id;
                }

                console.log("🏪 Setting store as current:", storeToSelect);
                setCurrentStoreId(storeToSelect);
                dispatch(setSelectedStoreId(storeToSelect));
            } else {
                console.log("🏪 No stores found");
            }
        } catch (error) {
            console.error("🏪 Error fetching stores:", error);
        }
    };


    useEffect(() => {
        if (businessId || user?.businessId?.[0]) {
            getAllStores();
        }
    }, [businessId, user?.businessId]);

    useEffect(() => {
        if (selectedStoreIdFromRedux) {
            setCurrentStoreId(selectedStoreIdFromRedux);
        } else if (defaultStore && !currentStoreId) {
            setCurrentStoreId(defaultStore);
        }
    }, [selectedStoreIdFromRedux, defaultStore, currentStoreId]);

    useEffect(() => {
        const matchingStore = allStores?.find(store => store._id === currentStoreId);
        if (matchingStore) {
            const storeOption = {
                value: matchingStore._id,
                label: matchingStore.title || matchingStore.name,
            };
            setSelectedStore(storeOption);
        } else {
            console.log("🏪 No matching store found, selectedStore remains:", selectedStore);
        }
    }, [allStores, currentStoreId]);

    useEffect(() => {
        if (currentStoreId) {
            setOrders([]);
            setTotalItems(0);
            setLoadingOrders(true);
            setCurrentPage(1); // Reset to first page when store changes
        }
    }, [currentStoreId]);

    const shouldFetchOrders = businessId && currentStoreId;

    // Removed illegal conditional hook call that was causing React crash
    // const {order_data} = shouldFetchOrders
    //     ? orderService.GetOrders(searchQuery, fetch)
    //     : {order_data: {orders: [], totalItems: 0}};

    // Real-time order event handlers
    const handleOrderCreated = useCallback(
        eventData => {
            const newOrder = eventData?.order || eventData;

            if (!newOrder || !newOrder._id) {
                console.log("[Socket] Invalid order data received:", eventData);
                return;
            }

            // Only add order if it belongs to the currently selected store
            // Check both new structure (store._id) and old structure (storeId)
            const orderStoreId = newOrder.store?._id || newOrder.storeId;
            if (orderStoreId === currentStoreId) {
                console.log("[Socket] ✅ New order created for selected store:", {
                    orderId: newOrder._id,
                    trackingId: newOrder.trackingId,
                    storeId: orderStoreId,
                    totalAmount: newOrder.pricing?.total || newOrder.totalAmountInCents,
                    status: newOrder.status,
                });

                // Trigger full-screen overlay + looping sound
                triggerNewOrderAlert(1);

                // Add to the beginning of the orders array (most recent first)
                setOrders(prev => [newOrder, ...prev]);
                setTotalItems(prev => prev + 1);
            } else {
                console.log("[Socket] ⏭️ New order created for different store, ignoring:", {
                    orderStoreId: newOrder.store?._id || newOrder.storeId,
                    currentStoreId: currentStoreId,
                });
            }
        },
        [currentStoreId, triggerNewOrderAlert]
    );

    const handleOrderUpdated = useCallback(
        eventData => {
            const data = eventData?.data || eventData;
            const {orderId, status, eventStatus, paymentStatus, order, driverInfo} = data;

            if (!orderId) {
                console.log("[Socket] Invalid order update - missing orderId:", eventData);
                return;
            }
            const orderStoreId = order?.store?._id || order?.storeId;
            if (orderStoreId && orderStoreId !== currentStoreId) {
                console.log("[Socket] ⏭️ Order updated for different store, ignoring:", {
                    orderStoreId,
                    currentStoreId,
                });
                return;
            }

            // ── Driver removal events (cancelled / rejected) ──────────────────────
            const driverRemovalEvents = [
                "driver_rejected",
                "driver_cancelled_pre_pickup",
                "driver_cancelled_post_pickup",
            ];
            const isDriverRemoval = driverRemovalEvents.includes(eventStatus);

            if (isDriverRemoval) {
                console.log(`[Socket] 🔄 Driver removal event (${eventStatus}) - clearing driver info for order ${orderId}`);
                // Clear the driver from the order card immediately for instant feedback
                setOrders(prev =>
                    prev.map(existingOrder => {
                        if (existingOrder._id !== orderId) return existingOrder;
                        return {
                            ...existingOrder,
                            // Clear driver information so the UI reverts to "Waiting for Driver"
                            fulfillment: {
                                ...existingOrder.fulfillment,
                                externalDriver: null,
                            },
                            deliveryInfo: null,
                        };
                    })
                );
                // Always refetch from API to get the fully updated order from the database
                setTimeout(async () => {
                    try {
                        console.log("[API] Refetching orders after driver removal event...");
                        const response = await orderService.getOrders(searchQueryRef.current);
                        if (response?.items) {
                            setOrders(response.items);
                            console.log("[API] Orders refetched successfully after driver removal");
                        }
                    } catch (error) {
                        console.error("[API] Failed to refetch orders after driver removal:", error);
                    }
                }, 1500);
                return; // Don't fall through to normal status update logic
            }

            setOrders(prev =>
                prev.map(existingOrder => {
                    if (existingOrder._id !== orderId) return existingOrder;

                    console.log("[Socket] ✅ Updating order:", {
                        orderId,
                        oldStatus: existingOrder.status,
                        newStatus: status,
                        paymentStatus,
                    });
                    if (status === "paid" || paymentStatus) {
                        return {
                            ...existingOrder,
                            paymentStatus: paymentStatus || "paid",
                        };
                    }
                    return {
                        ...existingOrder,
                        status,
                        // Update or clear driverInfo based on what the server sent
                        ...(driverInfo !== undefined && {
                            deliveryInfo: driverInfo,
                            fulfillment: {
                                ...existingOrder.fulfillment,
                                externalDriver: driverInfo,
                            },
                        }),
                    };
                })
            );

            // Refetch orders from API for delivery-related status changes to get latest data
            const deliveryStatuses = [
                "accepted",
                "preparing",
                "ready_for_pickup",
                "out_for_delivery",
                "ownership_taken",
                "ownership taken",
                "completed",
                "delivered",
            ];
            if (status && deliveryStatuses.includes(status.toLowerCase())) {
                console.log("[Socket] Delivery-related order_updated - refetching orders from API...");
                setTimeout(async () => {
                    try {
                        console.log("[API] Refetching orders after order_updated status change...");
                        const response = await orderService.getOrders(searchQueryRef.current);
                        if (response?.items) {
                            setOrders(response.items);
                            console.log("[API] Orders refetched successfully after order_updated");
                        }
                    } catch (error) {
                        console.error("[API] Failed to refetch orders:", error);
                    }
                }, 2000);
            }
        },
        [currentStoreId]
    );

    const findMatchingOrder = (order, deliveryData) => {
        const {jobId, bookingCode} = deliveryData;
        const orderJobId =
            order.integrations?.meta?.response?.job_id ||
            order.integrations?.externalOrderId ||
            order.externalSyncMeta?.job_id ||
            order.externalOrderId;
        const orderBookingCode =
            order.integrations?.meta?.bookingCode ||
            order.integrations?.meta?.response?.booking_code ||
            order.externalSyncMeta?.booking_code ||
            order.bookingCode;

        console.log("[Matching] Order data:", {
            orderId: order._id,
            orderJobId,
            orderBookingCode,
            orderExternalOrderId: order.integrations?.externalOrderId || order.externalOrderId,
            integrationsMeta: order.integrations?.meta,
            deliveryJobId: jobId,
            deliveryBookingCode: bookingCode,
        });

        // Priority 1: Exact matches (most reliable)
        // Match by job_id from integrations.meta.response.job_id (new structure)
        if (orderJobId && orderJobId === jobId) {
            return {matched: true, reason: "integrations.meta.response.job_id"};
        }

        // Match by externalOrderId from integrations.externalOrderId (new structure)
        if (order.integrations?.externalOrderId === jobId) {
            return {matched: true, reason: "integrations.externalOrderId"};
        }

        // Match by externalOrderId (old structure fallback)
        if (order.externalOrderId === jobId) {
            return {matched: true, reason: "externalOrderId"};
        }

        // Match by booking_code from integrations.meta.bookingCode (new structure)
        if (orderBookingCode && orderBookingCode === bookingCode) {
            return {matched: true, reason: "integrations.meta.bookingCode"};
        }

        // Match by booking_code from externalSyncMeta (old structure fallback)
        if (order.externalSyncMeta?.booking_code === bookingCode) {
            return {matched: true, reason: "externalSyncMeta.booking_code"};
        }

        // Match by bookingCode (direct field fallback)
        if (order.bookingCode === bookingCode) {
            return {matched: true, reason: "bookingCode"};
        }

        // Match by order._id (direct match)
        if (order._id === jobId) {
            return {matched: true, reason: "orderId"};
        }

        // Priority 2: Booking code component matching
        const bookingCodeToCheck = orderBookingCode || order.bookingCode;
        if (bookingCode && bookingCodeToCheck) {
            const orderBookingParts = bookingCodeToCheck.split("-");
            const jobBookingParts = bookingCode.split("-");

            // Match if same restaurant, same date, and exact order number
            if (
                orderBookingParts.length === 3 &&
                jobBookingParts.length === 3 &&
                orderBookingParts[0] === jobBookingParts[0] && // same restaurant
                orderBookingParts[1] === jobBookingParts[1] && // same date
                parseInt(orderBookingParts[2]) === parseInt(jobBookingParts[2])
            ) {
                // exact order number
                return {matched: true, reason: "bookingCodeComponents"};
            }

            // Match by order number from booking code
            const orderNumber = jobBookingParts[jobBookingParts.length - 1];
            if (order.trackingId === orderNumber || order._id === orderNumber) {
                return {matched: true, reason: "orderNumber"};
            }
        }

        // Priority 3: ID similarity matching (more specific, less prone to false positives)
        if (jobId && order._id) {
            // Match by last 16 characters of ID (specific enough to avoid conflicts)
            if (order._id.length >= 16 && jobId.length >= 16 && order._id.slice(-16) === jobId.slice(-16)) {
                return {matched: true, reason: "idSimilarity16"};
            }
        }

        return {matched: false, reason: "noMatch"};
    };

    const reloadOrders = useCallback(async () => {
        if (!shouldFetchOrders) {
            console.log("[API] Skipping order reload - missing businessId or storeId");
            return;
        }
        try {
            console.log("[API] Reloading orders to get updated integrations.meta...");
            const response = await orderService.getOrders(searchQuery);
            if (response?.items) {
                setOrders(response.items);
                console.log("[API] Orders reloaded successfully");
            }
        } catch (error) {
            console.error("[API] Failed to reload orders:", error);
        }
    }, [searchQuery, shouldFetchOrders]);

    // Separate function to handle order status updates
    const updateOrderStatus = useCallback((data, orderStatus) => {
        setOrders(prev => {
            console.log(
                "[Socket] Current orders before update:",
                prev.map(o => ({
                    id: o._id,
                    bookingCode: o.integrations?.meta?.bookingCode || o.bookingCode,
                    externalSyncMeta: o.externalSyncMeta,
                    integrations: o.integrations,
                    status: o.status,
                    orderNumber: o.trackingId,
                    externalOrderId: o.integrations?.externalOrderId || o.externalOrderId,
                }))
            );

            let foundMatch = false;
            let matchReason = "";

            const updated = prev.map(order => {
                const matchResult = findMatchingOrder(order, data);

                if (matchResult.matched) {
                    console.log("[Socket] ✅ Found matching order:", {
                        orderId: order._id,
                        orderBookingCode: order.integrations?.meta?.bookingCode || order.bookingCode,
                        orderExternalSyncMeta: order.externalSyncMeta,
                        orderIntegrations: order.integrations,
                        orderNumber: order.trackingId,
                        externalOrderId: order.integrations?.externalOrderId || order.externalOrderId,
                        jobId: data.jobId,
                        jobBookingCode: data.bookingCode,
                        newStatus: orderStatus,
                        matchReason: matchResult.reason,
                    });

                    foundMatch = true;
                    matchReason = matchResult.reason;

                    return {
                        ...order,
                        status: orderStatus,
                        deliveryInfo: {
                            driverId: data.driverId,
                            driverName: data.driverName,
                            status: data.status,
                            acceptedAt: data.acceptedAt,
                            bookingCode: data.bookingCode,
                            pickupCode: data.pickupCode,
                        },
                        fulfillment: {
                            ...order.fulfillment,
                            externalDriver: {
                                ...order.fulfillment?.externalDriver,
                                pickupCode: data.pickupCode,
                                name: data.driverName,
                                externalId: data.driverId,
                            },
                        },
                        // Set delivery timestamps based on status
                        delivery: {
                            ...order.delivery,
                            ...(orderStatus === "out_for_delivery" && {
                                startedAt: new Date().toISOString(),
                            }),
                            ...(orderStatus === "completed" && {
                                deliveredAt: new Date().toISOString(),
                            }),
                        },
                    };
                }

                return order;
            });

            if (!foundMatch) {
                console.log("[Socket] ❌ No matching order found for:", {
                    jobId: data.jobId,
                    bookingCode: data.bookingCode,
                    availableOrders: prev.map(o => ({
                        id: o._id,
                        bookingCode: o.integrations?.meta?.bookingCode || o.bookingCode,
                        externalSyncMeta: o.externalSyncMeta,
                        integrations: o.integrations,
                        orderNumber: o.trackingId,
                        externalOrderId: o.integrations?.externalOrderId || o.externalOrderId,
                    })),
                });
            } else {
                console.log(`[Socket] ✅ Order updated successfully using ${matchReason} matching`);
            }

            return updated;
        });
    }, []);

    const handleDeliveryJobStatus = useCallback(
        event => {
            const data = event?.detail;
            if (!data || !data.jobId) return;
            console.log("[Socket] Delivery job status update received:", data);
            let orderStatus = null;
            const normalizedStatus = data.status.toLowerCase();

            switch (normalizedStatus) {
                case "accepted":
                    orderStatus = "accepted";
                    break;
                case "ownership taken":
                case "ownership_taken": // handle both formats
                    orderStatus = "out_for_delivery";
                    break;
                case "completed":
                    orderStatus = "completed";
                    break;
                default:
                    console.log("[Socket] Unhandled delivery status:", data.status);
                    break;
            }

            if (orderStatus) {
                // Always refetch orders from API to get latest data (pickup code, driver info, etc.)
                console.log("[Socket] Delivery status changed - refetching orders from API...");
                // First apply status update immediately for instant UI feedback
                updateOrderStatus(data, orderStatus);
                // Then refetch from API after a delay to get the latest server data
                setTimeout(async () => {
                    try {
                        console.log("[API] Refetching orders after delivery status change...");
                        const response = await orderService.getOrders(searchQueryRef.current);
                        if (response?.items) {
                            setOrders(response.items);
                            console.log("[API] Orders refetched successfully after delivery status change");
                        }
                    } catch (error) {
                        console.error("[API] Failed to refetch orders:", error);
                    }
                }, 2000);
            }
        },
        [updateOrderStatus]
    );

    const handlePreparationTimeSet = useCallback(eventData => {
        const data = eventData?.detail || eventData;
        console.log("⏱️ [LiveOrders] processing preparation_time_set:", data);

        if (!data || !data.orderId) return;

        setOrders(prev =>
            prev.map(order => {
                if (order._id === data.orderId) {
                    return {
                        ...order,
                        status: "preparing",
                        preparation: data.preparation || order.preparation,
                        status: data.status || order.status,
                    };
                }
                return order;
            })
        );
    }, []);
    useEffect(() => {
        if (shouldFetchOrders) {
            console.log("Fetching orders for store:", currentStoreId, "page:", currentPage, "with query:", searchQuery);
            setLoadingOrders(true);
            orderService
                .getOrders(searchQuery)
                .then(response => {
                    console.log("Orders fetched:", response?.items?.length, "orders, total:", response?.totalItems);
                    setOrders(response?.items || []);
                    setTotalItems(response?.totalItems || 0);
                    setLoadingOrders(false);
                })
                .catch(error => {
                    console.error("Failed to fetch orders:", error);
                    setOrders([]);
                    setTotalItems(0);
                    setLoadingOrders(false);
                });
        } else {
            setOrders([]);
            setTotalItems(0);
            setLoadingOrders(false);
        }
    }, [shouldFetchOrders, currentStoreId, currentPage, searchQuery]);

    // Listen for order events from notification service
    useEffect(() => {
        const handleOrderCreatedEvent = event => {
            console.log("📦 [LiveOrders] Received order_created event:", event.detail);
            handleOrderCreated(event.detail);
        };

        const handleOrderUpdatedEvent = event => {
            console.log("📦 [LiveOrders] Received order_updated event:", event.detail);
            handleOrderUpdated(event.detail);
        };

        const handleIncomingOrderEvent = event => {
            console.log("📦 [LiveOrders] Received incoming_order event:", event.detail);
            handleOrderCreated(event.detail);
        };

        const handleDeliveryJobStatusEvent = event => {
            console.log("🚚 [LiveOrders] Received delivery_job_status event:", event.detail);
            handleDeliveryJobStatus(event);
        };

        const handlePreparationTimeSetEvent = event => {
            handlePreparationTimeSet(event);
        };

        // Add event listeners for all order-related events
        window.addEventListener("order_created", handleOrderCreatedEvent);
        window.addEventListener("order_updated", handleOrderUpdatedEvent);
        window.addEventListener("incoming_order", handleIncomingOrderEvent);
        window.addEventListener("delivery_job_status", handleDeliveryJobStatusEvent);
        window.addEventListener("preparation_time_set", handlePreparationTimeSetEvent);

        return () => {
            // Clean up event listeners
            window.removeEventListener("order_created", handleOrderCreatedEvent);
            window.removeEventListener("order_updated", handleOrderUpdatedEvent);
            window.removeEventListener("incoming_order", handleIncomingOrderEvent);
            window.removeEventListener("delivery_job_status", handleDeliveryJobStatusEvent);
            window.removeEventListener("preparation_time_set", handlePreparationTimeSetEvent);
        };
    }, [handleOrderCreated, handleOrderUpdated, handleDeliveryJobStatus, handlePreparationTimeSet]);

    const dishDescription = item => (
        <div className="dish-holder">
            <figure className="image-holder">
                <img src={item?.image || dishImg} alt="dishImg" />
            </figure>
            <div className="description">
                <span className="heading">{item?.name}</span>
                <span className="category">
                    <b>Category:&nbsp;</b>
                    {item?.category}
                </span>
                <div className="price-holder">
                    <span>From&nbsp;</span>
                    <span className="price">PKR {item?.price}</span>
                </div>
            </div>
        </div>
    );

    // Removed redundant data processing that used the broken order_data hook
    // const orderData = ...
    // const rowsData = ...

    const statusData = [
        {
            label: "Other Statuses",
            value: "All Status",
        },
        {
            label: "Accepted",
            value: "Accepted",
        },
        {
            label: "Completed",
            value: "Completed",
        },
        {
            label: "Cancelled",
            value: "Cancelled",
        },
    ];
    const typeData = [
        {
            label: "All Type",
            value: "All Type",
        },
        {
            label: "Dine-In",
            value: "Dine-In",
        },
        {
            label: "Pick-up",
            value: "Pick-up",
        },
        {
            label: "Delivery",
            value: "Delivery",
        },
    ];
    const newOrderData = [
        {
            label: "All",
            value: "All",
        },
        {
            label: "New",
            value: "new",
        },
        {
            label: "On Hold",
            value: "onHold",
        },
        {
            label: "Complete",
            value: "Complete",
        },
        {
            label: "Cancelled",
            value: "Cancelled",
        },
    ];

    return (
        <>
            {/* ─── New Order Full-Screen Overlay ────────────────────────── */}
            {showOverlay && (
                <NewOrderOverlay
                    onClick={dismissNewOrderAlert}
                    onMouseMove={dismissNewOrderAlert}
                    onTouchStart={dismissNewOrderAlert}
                >
                    <div className="overlay-content">
                        <div className="pulse-ring" />
                        <div className="order-count">{newOrderCount}</div>
                        <h2 className="overlay-title">
                            New Order{newOrderCount > 1 ? "s" : ""}
                        </h2>
                        <p className="overlay-subtitle">
                            Tap anywhere to dismiss
                        </p>
                    </div>
                </NewOrderOverlay>
            )}

            {/* ─── Collapsed Banner (after 30s auto-collapse) ──────────── */}
            {showBanner && !showOverlay && (
                <NewOrderBanner onClick={dismissNewOrderAlert}>
                    <div className="banner-pulse" />
                    <span className="banner-count">{newOrderCount}</span>
                    <span className="banner-text">
                        New Order{newOrderCount > 1 ? "s" : ""} — Tap to dismiss
                    </span>
                    <button className="banner-close" onClick={dismissNewOrderAlert}>
                        ✕
                    </button>
                </NewOrderBanner>
            )}

            <StyledLiveOrders>
                <div className="head-holder">
                    {/* <button onClick={() => handleActiveSideBar()} type="button" className="nav-opener">
                        <span />
                    </button> */}
                    <div className="col">
                        <div style={{display: "flex", alignItems: "center", gap: "20px", marginBottom: "10px"}}>
                            <h2 className="holder-title">Live Orders</h2>
                        </div>
                        {/* <div className="sidebar-head">
                            <Select
                                placeholder="Search by name or order id"
                                options={newOrderData}
                                prefix={<IoSearchOutline size={20} />}
                                noMargin
                            />
                        </div> */}
                    </div>
                    <div className="col filters-col" style={{display: "flex", alignItems: "center", gap: "10px", flexWrap: "nowrap"}}>

                        <div
                            className="custom-date-picker"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                                background: "#fff",
                                padding: "8px 12px",
                                borderRadius: "24px",
                                border: "1px solid #ddd",
                            }}>
                            <input
                                type="date"
                                value={customDateRange.startDate}
                                onChange={e => setCustomDateRange(prev => ({...prev, startDate: e.target.value}))}
                                max={customDateRange.endDate || undefined}
                                style={{border: "none", background: "transparent", outline: "none", cursor: "pointer", fontSize: "14px", color: "#333"}}
                            />
                            {customDateRange.startDate && (
                                <>
                                    <span style={{color: "#666", fontSize: "14px"}}>to</span>
                                    <input
                                        type="date"
                                        value={customDateRange.endDate}
                                        onChange={e => setCustomDateRange(prev => ({...prev, endDate: e.target.value}))}
                                        min={customDateRange.startDate || undefined}
                                        style={{border: "none", background: "transparent", outline: "none", cursor: "pointer", fontSize: "14px", color: "#333"}}
                                    />
                                </>
                            )}
                        </div>
                        <div className="sidebar-head filter">
                            <Select
                                placeholder="All Status"
                                options={statusData}
                                value={statusFilter}
                                onChange={({target: {value}}) => setStatusFilter(value)}
                                suffix={<IoIosArrowDown size={20} />}
                                noMargin
                            />
                        </div>
                        <div className="sidebar-head filter">
                            <Select
                                placeholder="All Type"
                                options={typeData}
                                value={typeFilter}
                                onChange={({target: {value}}) => setTypeFilter(value)}
                                suffix={<IoIosArrowDown size={20} />}
                                noMargin
                            />
                        </div>
                        {(statusFilter.value !== "All Status" || typeFilter.value !== "All Type" || customDateRange.startDate !== "" || customDateRange.endDate !== "" || searchText !== "") && (
                            <div>
                                <button
                                    type="button"
                                    style={{
                                        border: "1px solid #ddd",
                                        background: "#fff",
                                        color: "#333",
                                        padding: "8px 16px",
                                        borderRadius: "24px",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                        fontWeight: "500",
                                        transition: "background 0.3s",
                                    }}
                                    onClick={() => {
                                        setStatusFilter({label: "All Status", value: "All Status"});
                                        setTypeFilter({label: "All Type", value: "All Type"});
                                        setCustomDateRange({startDate: "", endDate: ""});
                                        setSearchText("");
                                    }}
                                    onMouseEnter={e => (e.target.style.background = "#f5f5f5")}
                                    onMouseLeave={e => (e.target.style.background = "#fff")}>
                                    Reset
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                <div className="tabs-wrap">
                    <Button
                        variant={statusFilter.value === "All Status" ? "white" : "transparent"}
                        onClick={() => setStatusFilter({label: "All Status", value: "All Status"})}
                        className={`btn ${statusFilter.value === "All Status" ? "active-btn" : ""}`}>
                        All Orders
                        {statusFilter.value === "All Status" && (
                            <span className="count">{loadingOrders ? "..." : totalItems}</span>
                        )}
                    </Button>
                    <Button
                        variant={statusFilter.value === "New" ? "white" : "transparent"}
                        onClick={() => setStatusFilter({label: "New", value: "New"})}
                        className={`btn ${statusFilter.value === "New" ? "active-btn" : ""}`}>
                        <div className="dot new-dot"></div>
                        New
                        {statusFilter.value === "New" && (
                            <span className="count">{loadingOrders ? "..." : totalItems}</span>
                        )}
                    </Button>
                    <Button
                        variant={statusFilter.value === "Preparing" ? "white" : "transparent"}
                        onClick={() => setStatusFilter({label: "Preparing", value: "Preparing"})}
                        className={`btn ${statusFilter.value === "Preparing" ? "active-btn" : ""}`}>
                        <div className="dot preparing-dot"></div>
                        Preparing
                        {statusFilter.value === "Preparing" && (
                            <span className="count">{loadingOrders ? "..." : totalItems}</span>
                        )}
                    </Button>
                    <Button
                        variant={statusFilter.value === "Ready" ? "white" : "transparent"}
                        onClick={() => setStatusFilter({label: "Ready", value: "Ready"})}
                        className={`btn ${statusFilter.value === "Ready" ? "active-btn" : ""}`}>
                        <div className="dot ready-dot"></div>
                        Ready
                        {statusFilter.value === "Ready" && (
                            <span className="count">{loadingOrders ? "..." : totalItems}</span>
                        )}
                    </Button>
                </div>
                {/* <div className="orders-wrap">
                    {OrdersData?.map((data, index) => (
                        <>
                            <OrderCard key={index} data={data} />
                        </>
                    ))}
                </div> */}

                {!shouldFetchOrders ? (
                    <div
                        className="empty-state"
                        style={{
                            textAlign: "center",
                            padding: "60px 20px",
                            color: "#666",
                        }}>
                        <h3>No Orders Found</h3>
                    </div>
                ) : loadingOrders ? (
                    <div style={{minHeight: "400px", position: "relative"}}>
                        <Loaders viewLoader />
                    </div>
                ) : (
                    <div className="orders-wrap">
                        {orders?.length > 0 ? (
                            orders
                                .filter(
                                    order =>
                                        order?.payment?.status === "paid" ||
                                        order?.paymentStatus === "paid" ||
                                        order?.payment?.status === "cod_pending" ||
                                        order?.paymentStatus === "cod_pending" ||
                                        order?.payment?.status === "collected" ||
                                        order?.paymentStatus === "collected"
                                )
                                .map((order, index) => (
                                    <OrderCard
                                        key={order?._id || index}
                                        index={index}
                                        orderId={order?._id}
                                        data={formatApiOrder(order)}
                                        rawOrderData={order}
                                        storeName={selectedStore?.label || "Restaurant"}
                                        onStatusChange={(newStatus, additionalData = {}, fullUpdatedOrder = null) => {
                                            const orderId = order._id;
                                            setOrders(prev =>
                                                prev.map(o => {
                                                    if (o._id !== orderId) return o;

                                                    // If the API returned the fully updated order, use it entirely!
                                                    // This guarantees we have the pickup code instantly.
                                                    if (fullUpdatedOrder) {
                                                        return fullUpdatedOrder;
                                                    }

                                                    const updatedOrder = {
                                                        ...o,
                                                        status: newStatus,
                                                        updatedAt: new Date().toISOString(),
                                                    };

                                                    // If accepting with estimatedMinutes, set prep timing
                                                    if (additionalData.estimatedMinutes) {
                                                        const now = new Date();
                                                        const estimatedReadyAt = new Date(
                                                            now.getTime() + additionalData.estimatedMinutes * 60 * 1000
                                                        ).toISOString();
                                                        updatedOrder.preparation = {
                                                            ...o.preparation,
                                                            setAt: now.toISOString(),
                                                            estimatedReadyAt,
                                                        };
                                                    }

                                                    // If marking ready, set preparation.readyAt
                                                    if (newStatus === "ready_for_pickup" || newStatus === "ready") {
                                                        updatedOrder.preparation = {
                                                            ...updatedOrder.preparation,
                                                            readyAt: new Date().toISOString(),
                                                        };
                                                    }

                                                    return updatedOrder;
                                                })
                                            );
                                        }}
                                    />
                                ))
                        ) : (
                            <div
                                style={{
                                    textAlign: "center",
                                    padding: "40px 20px",
                                    color: "#999",
                                }}>
                                <p>No paid orders found for this page.</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Pagination */}
                {shouldFetchOrders && totalItems > pageSize && (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "20px 0",
                            marginTop: "20px",
                        }}>
                        <Pagination
                            currentPage={currentPage}
                            totalCount={totalItems}
                            pageSize={pageSize}
                            onPageChange={page => {
                                console.log("📄 Changing to page:", page);
                                setCurrentPage(page);
                            }}
                            siblingCount={1}
                        />
                    </div>
                )}

                {/* <OrderHandler
                    orders={orders}
                    setOrders={setOrders}
                    totalItems={totalItems}
                    order_data={order_data}
                    rowsData={rowsData}
                    columnNames={columnNames}
                    searchQuery={searchQuery}
                /> */}
            </StyledLiveOrders>
        </>
    );
};

export default LiveOrder;
